import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FileService, IStorage, LocalStorage } from '@isophi/core-legacy';
import { DocumentPreviewService, IconService, ToastService } from '@isophi/core-ui';
import { ActivityListReport, PortfolioManagerReportsService } from '@isophi/isophi-pdfs-api';
import { ManagerActivity, ManagerActivityStat, PaginatedManagerActivityList, PortfolioService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, switchMap, takeUntil, tap } from 'rxjs';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { BaseComponent } from '../../../../shared/components/base/base.component';
import { LocalStorageConstService } from '../../../../shared/services/local-storage-const.service';
import { PortfolioFilterCriteria } from '../../constants/models';

@Component({
  selector: 'app-pap-portfolio',
  templateUrl: './pap-portfolio.component.html',
  styleUrls: ['./pap-portfolio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PapPortfolioComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly helperService = inject(HelperService);
  private readonly translocoService = inject(TranslocoService);
  protected readonly iconService = inject(IconService);
  private readonly router = inject(Router);
  private readonly routerLinkFactory = inject(RouterLinkFactory);
  private readonly porfolioService = inject(PortfolioService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly toastService = inject(ToastService);
  private readonly fileService = inject(FileService);
  private readonly documentPreviewService = inject(DocumentPreviewService);
  private readonly portfolioManagerReportsService = inject(PortfolioManagerReportsService);
  private readonly lSConstService = inject(LocalStorageConstService);
  private readonly localStorage: IStorage = new LocalStorage();

  portfolioActivityList$: Observable<PaginatedManagerActivityList>;

  portfolioActivityStats$: Observable<ManagerActivityStat>;

  private portfolioActivityReload$ = new BehaviorSubject<void>(null);

  private filterCriteria: PortfolioFilterCriteria = {
    search: null,
    teacherId: null,
    dateQuickRangeKey: null,
    dateStart: '',
    dateEnd: '',
  };

  protected page = 1;
  protected limit = 10;
  protected offset = 0;

  ngOnInit(): void {
    this.helperService.setTitle(undefined, this.translocoService.translate('pap/portfolio/title'));
    const saved = this.localStorage.getItem(this.lSConstService.papPortfolioPageLimit);
    if (saved) {
      this.limit = +saved;
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    this.helperService.clearSmallTextTitle();
    super.ngOnDestroy();
  }

  loadData(): void {
    this.portfolioActivityList$ = this.portfolioActivityReload$.pipe(
      switchMap(() =>
        this.porfolioService.portfolioActivitiesList(
          undefined,
          undefined,
          undefined,
          this.filterCriteria.dateEnd,
          undefined,
          this.filterCriteria.dateStart,
          undefined,
          this.limit,
          this.offset,
          undefined,
          undefined,
          this.filterCriteria.search,
          undefined,
          undefined,
          this.filterCriteria.teacherId
        )
      ),
      tap((portfolioActivityList) => {
        const portfolioActivityText =
          portfolioActivityList.count > 1
            ? this.translocoService.translate('pap/portfolio/portfolioActivities')
            : this.translocoService.translate('pap/portfolio/portfolioActivity');
        if (portfolioActivityList.count > 0) {
          this.helperService.setSmallTextTitle(`${portfolioActivityList.count} ${portfolioActivityText}`);
        }
      })
    );

    this.portfolioActivityStats$ = this.portfolioActivityReload$.pipe(
      switchMap(() =>
        this.porfolioService.portfolioActivitiesStatsRetrieve(
          undefined,
          undefined,
          undefined,
          this.filterCriteria.dateEnd,
          undefined,
          this.filterCriteria.dateStart,
          undefined,
          undefined,
          undefined,
          this.filterCriteria.search,
          undefined,
          undefined,
          this.filterCriteria.teacherId
        )
      )
    );
  }

  openPortfolioActivity(portfolioActivity: ManagerActivity): void {
    this.router.navigate(this.routerLinkFactory.papPortfolioDetail(portfolioActivity.id));
  }

  onPageChange(page: number): void {
    this.offset = (page - 1) * this.limit;
    this.reloadPortfolioActivities();
  }

  onLimitChange(value: number): void {
    this.limit = value;
    this.page = 1;
    this.offset = 0;
    this.localStorage.setItem(this.lSConstService.papPortfolioPageLimit, String(value));
    this.reloadPortfolioActivities();
  }

  reloadPortfolioActivities(): void {
    this.portfolioActivityReload$.next();
  }

  setFilter(filterCriteria: PortfolioFilterCriteria): void {
    this.filterCriteria = filterCriteria;
    this.page = 1;
    this.offset = 0;
    this.reloadPortfolioActivities();
  }

  exportPage(type: 'xlsx' | 'csv'): void {
    this.spinner.show();
    this.porfolioService
      .portfolioActivitiesPrintRetrieve(
        undefined,
        undefined,
        undefined,
        this.filterCriteria.dateEnd,
        undefined,
        this.filterCriteria.dateStart,
        undefined,
        undefined,
        undefined,
        this.filterCriteria.search,
        undefined,
        undefined,
        this.filterCriteria.teacherId,
        type
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (data: Blob) => {
          this.fileService.downloadFile(
            data,
            `${this.translocoService.translate('file/activities')}.xlsx`,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          );
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('pap/portfolio/exportErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  printPage(): void {
    this.spinner.show();
    const body: ActivityListReport = {
      query: {
        created__date__gte: this.filterCriteria.dateStart,
        created__date__lte: this.filterCriteria.dateEnd,
        search: this.filterCriteria.search,
        teacher_id: this.filterCriteria.teacherId,
      },
    };
    this.portfolioManagerReportsService
      .getManagerActivityListReport(body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (data: Blob) => {
          this.documentPreviewService.openBlob(data, `${this.translocoService.translate('file/activities')}.pdf`, 'application/pdf');
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('pap/plan/exportErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  computePaidByKindergarten(portfolioActivityStats: ManagerActivityStat): number {
    return Number(portfolioActivityStats.totalPrice) - Number(portfolioActivityStats.totalSelfPayerPrice);
  }
}
